import { useRef, useState } from 'react';
import { functionHeaders } from '@dia/db';
import { useT } from '@dia/i18n';
import { Button, cn } from '@dia/ui';
import { SUPABASE_ANON_KEY, SUPABASE_URL } from '../env';
import { supabase } from '../supabase';
import { RequireAuth, type Identity } from './lib/identity';
import { Icon } from './lib/icons';
import { loadStaffDirectory, useLoad } from './lib/data';
import { buildPairingLink, whatsappLink } from './lib/pairing';
import { Card, Field, inputClass, Notice, Page } from './lib/ui';

/**
 * Pair a phone. The manager or owner picks whose phone it is and the
 * staff-login function makes a one time code. The code comes back once, is
 * shown as a link, and is never stored here. The person opens the link on
 * their phone, and from then on their PIN is enough.
 */
export function JefeTelefonos() {
  return <RequireAuth need="manager">{(who) => <PhonesBody who={who} />}</RequireAuth>;
}

type Made = { link: string; minutes: number };

type Problem =
  | { key: 'ops.phones.need_signal' | 'ops.phones.denied' | 'ops.phones.session_lost' | 'ops.phones.pick_first' | 'ops.phones.copy_failed' }
  | { key: 'ops.phones.failed'; status: number };

/**
 * The token to send right now. An owner's Supabase Auth token is refreshed in
 * the background and lives an hour, so the one held in React state can be a
 * refresh behind. Asking the auth client at the moment of the call always gets
 * the current one, or nothing when the session is really gone.
 */
async function currentToken(who: Identity): Promise<string | null> {
  if (who.kind !== 'owner') return who.token;
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return who.token;
  }
}

function PhonesBody({ who }: { who: Identity }) {
  const { t } = useT();
  const directory = useLoad(loadStaffDirectory, 'directory');
  const [personId, setPersonId] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState<Made | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [copied, setCopied] = useState(false);
  const linkField = useRef<HTMLInputElement>(null);

  const people = directory.data ?? [];

  const make = async () => {
    if (!personId) {
      setProblem({ key: 'ops.phones.pick_first' });
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setProblem({ key: 'ops.phones.need_signal' });
      return;
    }
    setBusy(true);
    setProblem(null);
    setMade(null);
    try {
      const token = await currentToken(who);
      if (!token) {
        setProblem({ key: 'ops.phones.session_lost' });
        return;
      }
      const response = await fetch(`${SUPABASE_URL}/functions/v1/staff-login`, {
        method: 'POST',
        headers: functionHeaders({ anonKey: SUPABASE_ANON_KEY, token }),
        body: JSON.stringify({ action: 'create_pairing', staff_user_id: personId, label: label.trim() || undefined }),
      });
      const body = (await response.json().catch(() => ({}))) as { code?: string; expires_in_minutes?: number };
      if (response.ok && body.code) {
        setMade({ link: buildPairingLink(window.location.origin, body.code), minutes: body.expires_in_minutes ?? 30 });
        return;
      }
      // Say which refusal it was. One line for every failure made a signed out
      // owner, a crew phone and a server fault all look the same.
      if (response.status === 401) setProblem({ key: 'ops.phones.session_lost' });
      else if (response.status === 403) setProblem({ key: 'ops.phones.denied' });
      else setProblem({ key: 'ops.phones.failed', status: response.status });
    } catch {
      setProblem({ key: 'ops.phones.need_signal' });
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!made) return;
    try {
      await navigator.clipboard.writeText(made.link);
    } catch {
      // No clipboard API (an insecure page, or a browser that refuses it). The
      // old way still works from a tap: select the field and copy it.
      const field = linkField.current;
      field?.focus();
      field?.select();
      let ok = false;
      try {
        ok = Boolean(document.execCommand?.('copy'));
      } catch {
        ok = false;
      }
      if (!ok) {
        setCopied(false);
        setProblem({ key: 'ops.phones.copy_failed' });
        return;
      }
    }
    setProblem(null);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Page title={t('ops.phones.title')} back="/jefe">
      <div className="flex flex-col gap-5">
        <fieldset className="m-0 border-0 p-0">
          <legend className="mb-2 font-medium">{t('ops.phones.who')}</legend>
          {people.length === 0 && <Notice tone="warn">{t('ops.phones.no_people')}</Notice>}
          <div className="flex flex-col gap-2">
            {people.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={personId === p.id}
                onClick={() => {
                  setPersonId(p.id);
                  setMade(null);
                }}
                className={cn(
                  'min-h-tap rounded border px-4 py-3 text-left text-[18px]',
                  personId === p.id ? 'border-accent bg-accent font-semibold text-onAccent' : 'border-line bg-surface text-ink',
                )}
              >
                {p.name} ({t(`ops.phones.role_${p.role}`)})
              </button>
            ))}
          </div>
        </fieldset>

        <Field label={t('ops.phones.label')}>
          <input value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} maxLength={40} />
        </Field>

        <Button size="lg" block busy={busy} busyLabel={t('loading')} onClick={() => void make()}>
          <Icon name="phone" /> {t('ops.phones.make')}
        </Button>

        {problem && (
          <Notice tone={problem.key === 'ops.phones.denied' || problem.key === 'ops.phones.failed' ? 'danger' : 'warn'}>
            {problem.key === 'ops.phones.failed' ? t(problem.key, { status: problem.status }) : t(problem.key)}
          </Notice>
        )}

        {made && (
          <Card>
            <h2 className="mb-2 text-[1.15em] font-semibold">{t('ops.phones.link')}</h2>
            <input ref={linkField} readOnly value={made.link} onFocus={(e) => e.currentTarget.select()} className={`${inputClass} font-mono`} aria-label={t('ops.phones.link')} />
            <p className="mt-2 text-inkMuted">{t('ops.phones.expires', { minutes: made.minutes })}</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Button variant="secondary" onClick={() => void copy()}>
                {copied ? t('ops.phones.copied') : t('ops.phones.copy')}
              </Button>
              <a
                href={whatsappLink(t('ops.phones.whatsapp_text', { link: made.link }))}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-tap items-center justify-center rounded border border-line bg-surface px-3 text-center text-ink no-underline"
              >
                {t('ops.phones.whatsapp')}
              </a>
            </div>
            <p className="mt-3 text-inkMuted">{t('ops.phones.qr_note')}</p>
          </Card>
        )}
      </div>
    </Page>
  );
}
